import { Action, Point, Stage } from "./internalisation";
import { PartialThread } from "./message";
import { ActionData, AddActionMessageType, AddPointMessageType, AddStageMessageType, EmittableMessageData, InitializeThreadType, MessageData, PointData, StageData, Thread, ThreadData, UpdateMessageType } from "./types";

export class AddStageMessage implements AddStageMessageType {
  public readonly messageType = 'add_stage'
  public readonly messageId: string;
  public readonly stage: StageData;
  
  constructor({messageId, stage}: {
    messageId: string,
    stage: StageData,
  }) {
    this.messageId = messageId;
    this.stage = stage;
  }

  static fromData(data: any): AddStageMessage {
    return new AddStageMessage({
      messageId: data.messageId,
      stage: data.stage,
    });
  }

  attach(thread: Thread): void {
    if (thread.getMessageById(this.messageId) === undefined) {
      throw new Error(`Message with id ${this.messageId} not found`);
    }
    thread.getMessageById(this.messageId)?.internalisation.addStage(Stage.fromData(this.stage));
  }
}

export class AddActionMessage implements AddActionMessageType {
  public readonly messageType = 'add_action'
  public readonly messageId: string;
  public readonly stageId: string;
  public readonly action: ActionData;
  constructor({messageId, stageId, action}: {
    messageId: string,
    stageId: string,
    action: ActionData,
  }) {
    this.messageId = messageId;
    this.stageId = stageId;
    this.action = action;
  }

  static fromData(data: any): AddActionMessage {
    return new AddActionMessage({
      messageId: data.messageId,
      stageId: data.stageId,
      action: data.action,
    });
  }

  attach(thread: Thread): void {
    const message = thread.getMessageById(this.messageId);
    if (message === undefined) {
      throw new Error(`Message with id ${this.messageId} not found`);
    }
    const stage = message.internalisation.getStageById(this.stageId);
    if (stage === undefined) {
      throw new Error(`Stage with id ${this.stageId} not found in message ${this.messageId}`);
    }
    stage.addAction(Action.fromData(this.action));
  }
}

export class AddPointMessage implements AddPointMessageType {
  public readonly messageType = 'add_point'
  public readonly messageId: string
  public readonly stageId: string
  public readonly point: PointData
  public readonly actionId?: string
  constructor({messageId, stageId, point, actionId}:{
    messageId: string,
    stageId: string,
    point: PointData,
    actionId?: string
  }) {
    this.messageId = messageId;
    this.stageId = stageId;
    this.point = point;
    this.actionId = actionId;
  }

  static fromData(data: any): AddPointMessage {
    return new AddPointMessage({
      messageId: data.messageId,
      stageId: data.stageId,
      point: data.point,
      actionId: data.actionId
    });
  }

  attach(thread: Thread): void {
    const message = thread.getMessageById(this.messageId);
    if (message === undefined) {
      throw new Error(`Message with id ${this.messageId} not found`);
    }
    const stage = message.internalisation.getStageById(this.stageId);
    if (stage === undefined) {
      throw new Error(`Stage with id ${this.stageId} not found in message ${this.messageId}`);
    }
    if (this.actionId) {
      const action = stage.getActionOrPointById(this.actionId);
      if (action === undefined) {
        throw new Error(`Action with id ${this.actionId} not found in stage ${this.stageId} of message ${this.messageId}`);
      }
      if (action instanceof Action) {
        action.addPoint(Point.fromData(this.point));
      }
      else {
        throw new Error(`Action ID not an action in stage ${this.stageId} of message ${this.messageId}: ${this.actionId}`);
      }
    }
    else {
      stage.addPoint(Point.fromData(this.point));
    }
  }
}

export class UpdateMessage implements UpdateMessageType {
  public readonly messageType = 'update_message';
  public readonly message: MessageData;
  constructor({message}: {message: MessageData}) {
    this.message = message;
  }

  attach(thread: Thread): void {
    thread.getMessageById(this.message.id)?.setContent(this.message.content);
    thread.getMessageById(this.message.id)?.setLinks(this.message.links);
    thread.getMessageById(this.message.id)?.setMetadata(this.message.metadata);
    thread.getMessageById(this.message.id)?.setStatus(this.message.status);
  }

  static fromData(data: any): UpdateMessage {
    return new UpdateMessage({
      message: data.message
    });
  }
}

export class InitializeThread implements InitializeThreadType {
  public readonly messageType = 'initialize_thread'
  public readonly thread: ThreadData
  constructor(thread: ThreadData) {
    this.thread = thread;
  }

  attach(thread: Thread): void {
    const partial = PartialThread.fromData(this.thread);
    partial.messages.forEach((message) => thread.setMessage(message));
  }

  static fromData(data: any): InitializeThread {
    return new InitializeThread(data.thread);
  }
}

export function toObject(message: EmittableMessageData) {
  switch (message.messageType) {
    case 'add_stage':
      return AddStageMessage.fromData(message);
    case 'add_action':
      return AddActionMessage.fromData(message);
    case 'add_point':
      return AddPointMessage.fromData(message);
    case 'update_message':
      return UpdateMessage.fromData(message);
    case 'initialize_thread':
      return InitializeThread.fromData(message);
    default:
      throw new Error(`Unknown message type: ${message.messageType}`); 
  }
}